/**
 * Versioned marketing SMS consent language registry.
 *
 * All surfaces that display marketing SMS consent copy should import
 * MARKETING_SMS_CONSENT from here.
 */
import React from 'react';

import styled from '@emotion/styled';
import { Box } from '@opendoor/bricks-next/components/Box';
import type { BoxProps } from '@opendoor/bricks-next/components/Box';
import { Link } from '@opendoor/bricks-next/components/Link';
import { Text } from '@opendoor/bricks-next/components/Text';
import type { TextProps } from '@opendoor/bricks-next/components/Text';

// ---------------------------------------------------------------------------
// Consent language registry
// ---------------------------------------------------------------------------

export interface CtaAgreementLinkedProps {
  ctaText: string;
  tosAnalyticsName: string;
  privacyAnalyticsName: string;
  tosUrl?: string;
  privacyUrl?: string;
  linkProps?: Record<string, unknown>;
}

interface ConsentLanguageEntry {
  checkboxLabel: string;
  ctaAgreement: (ctaText: string) => string;
  full: (ctaText: string) => string;
  CtaAgreementLinked: React.FC<CtaAgreementLinkedProps>;
}

// DO NOT MODIFY EXISTING ENTRIES. IF YOU NEED TO UPDATE THE CONSENT LANGUAGE, ADD A NEW ENTRY TO THE OBJECT.
// WHEN LEGAL UPDATES THE COPY:
//   1. Add a new entry to CONSENT_VERSIONS (do NOT modify existing entries)
//   2. Update CURRENT_VERSION to point to the new key
//   3. Update the test
const CONSENT_VERSIONS: Record<string, ConsentLanguageEntry> = {
  marketing_sms_v1: {
    checkboxLabel:
      'I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase.',
    ctaAgreement: (ctaText: string) =>
      `By pressing \u201c${ctaText},\u201d I agree to Opendoor\u2019s Terms of Service and Privacy Policy.`,
    full: (ctaText: string) =>
      `I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase. By pressing \u201c${ctaText},\u201d I agree to Opendoor\u2019s Terms of Service and Privacy Policy.`,
    CtaAgreementLinked: ({
      ctaText,
      tosAnalyticsName,
      privacyAnalyticsName,
      tosUrl = 'https://www.opendoor.com/w/terms',
      privacyUrl = 'https://www.opendoor.com/w/privacy',
      linkProps = {},
    }) => (
      <>
        By pressing &ldquo;{ctaText},&rdquo; I agree to Opendoor&rsquo;s{' '}
        <Link
          analyticsName={tosAnalyticsName}
          href={tosUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Opens a new page with Opendoor’s terms of service"
          {...linkProps}
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          analyticsName={privacyAnalyticsName}
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Opens a new page with Opendoor’s privacy policy"
          {...linkProps}
        >
          Privacy Policy
        </Link>
        .
      </>
    ),
  },
};

export type ConsentVersion = keyof typeof CONSENT_VERSIONS;

const CURRENT_VERSION: ConsentVersion = 'marketing_sms_v1';

export const MARKETING_SMS_CONSENT = {
  version: CURRENT_VERSION,
  ...CONSENT_VERSIONS[CURRENT_VERSION],
};

export { CONSENT_VERSIONS };

// ---------------------------------------------------------------------------
// Shared auth UI components
// ---------------------------------------------------------------------------

export const EmailDisclaimerText: React.FC<{ analyticsName: string }> = ({ analyticsName }) => {
  return (
    <Text
      tag="p"
      typography="$bodyXsmall"
      whiteSpace="normal"
      $largerThanSM={{ whiteSpace: 'nowrap' }}
    >
      <MARKETING_SMS_CONSENT.CtaAgreementLinked
        ctaText="Continue"
        tosAnalyticsName={`${analyticsName}-terms`}
        privacyAnalyticsName={`${analyticsName}-privacy-policy`}
        linkProps={{ variant: 'primary', size: 'xsmall' }}
      />
    </Text>
  );
};

export const AccountFormHeader: React.FC<Omit<TextProps, 'typography' | 'tag'>> = ({
  children,
  ...rest
}) => {
  return (
    <Text tag="h1" typography="$headerMedium" {...rest} marginBottom="$10x" textAlign="left">
      {children}
    </Text>
  );
};

export const AccountFormWrapper: React.FC<BoxProps> = (props) => {
  return <Box width="100%" {...props} />;
};

export const FormWrapper: React.FC<BoxProps> = (props) => {
  return <Box width="100%" flexDirection="column" {...props} />;
};

export type DisclaimerProps = {
  location?: 'top' | 'bottom';
  formatText?: (formatProps: { actionText: string }) => string;
};

export const PHONE_DEFAULT_DISCLAIMER = (actionText: string) => {
  return MARKETING_SMS_CONSENT.full(actionText);
};

// eslint-disable-next-line no-restricted-syntax
export const Form = styled.form`
  width: 100%;
  margin-top: 28px; /* $7x token equivalent */
`;

export const Disclaimer: React.FC<
  BoxProps & { desktopNoWrap?: boolean; shouldRenderDisclaimerAboveCTA?: boolean }
> = ({ children, desktopNoWrap = false, shouldRenderDisclaimerAboveCTA, ...props }) => {
  return (
    <Box {...props}>
      <Text
        tag="p"
        typography="$bodyXsmall"
        color="$contentSecondary"
        whiteSpace="normal"
        $largerThanSM={desktopNoWrap ? { whiteSpace: 'nowrap' } : undefined}
      >
        {children}
      </Text>
    </Box>
  );
};
